import React, {useContext, useState} from "react";
import SelectBox from "../SelectBox";
import RequiredInfo from "../../../../Common/RequiredInfo";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import AppButton from "../../../../Common/AppButton";
import RemoveCircleIcon from "@material-ui/icons/RemoveCircle";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import useStyles from "./styles";
import PriorAuthorizationContext from "../../../../../contexts/PriorAuthorization/PriorAuthorization.context";

const maxDrugCompounds = 27;

const options = new Array(maxDrugCompounds)
    .fill("")
    .filter((item, i) => i <= 23)
    .map((item, i) => i + 2);

const validations = {
    productId: {
        fn: value => /^(?=[0-9]*$)(?:.{9}|.{11})$/.test(value),
        message: `Product ID(NDC) must be 9 or 11 digits.`
    },
    ingredientQty: {
        fn: value => /[A-Za-z]/.test(value) === false && parseFloat(value) > 0,
        message: `Ingredient Quantity must be a positive number`
    }
}


export default function CompoundTable({onChange, compoundIngredientCount, compounds, errors}) {

    const {setModalState} = useContext(PriorAuthorizationContext)

    if (errors.length === 0) {
        const dups = { productId: {}, ingredientQty: {}};
        let hasErrors = false;
        if (compounds && compounds.length) {
            compounds.forEach((compound, index) => {
                const row = {};
                ['productId', 'ingredientQty'].forEach(field => {
                    let message = '';
                    let isValid = validations[field].fn(compound[field]);
                    const isDup = field === 'productId' && dups[field] && dups[field][compound[field]];
                    if (!hasErrors && (!isValid || isDup)) {
                        hasErrors = true;
                    }

                    if(isValid && isDup) {
                        isValid = false;
                        message = 'This field must be unique';
                    } else if (!isValid) {
                        message = validations[field].message;
                    }
                    row[field] = {isValid, message};
                    dups[field][compound[field]] = 1;
                })
                errors.push(row);
            })
        }
    }

    const fieldStateDefault = () => {
        const value = [];
        if (compounds && compounds.length) {
            compounds.forEach(() => {
                const row = {};
                ['productId', 'ingredientQty'].forEach(field => {
                   row[field] = false;
                })
                value.push(row);
            })
        }
        return value;
    }

    const [fieldState, setFieldState] = useState(fieldStateDefault());

    const classes = useStyles();

    const addRow = () => {
        const newCompounds = [...compounds];
        const newCount = compoundIngredientCount + 1;
        if (newCount < maxDrugCompounds - 1) {
            newCompounds.push({productIdQlfr: "03"})
            handleCompoundIngredientChange(newCount, newCompounds);
        }
    }

    const closeModal = () => {
        setModalState({open: false})
    }

    const modalDefaults = {
        confirmText: 'Yes, Remove',
        confirmToolTip: 'Confirm Remove',
        denyText: 'No, Do Not Remove',
        denyToolTip: 'Do Not Remove',
        open: true,
        deny: closeModal,
        title: 'Are you sure you want to remove the data? All previously entered information will be lost.'
    }

    const changeCountConfimration = (compoundIngredientCount, compounds) => {
        setModalState({
            ...modalDefaults,
            confirm: () => handleCompoundIngredientChange(compoundIngredientCount, compounds),
        })
    }

    const compoundIngredientChange = e => {
        const {value} = e.target;
        const newCompounds = new Array(value + 1)
            .fill("")
            .filter((item, i) => i)
            .map((item, i) => ({
                ingredientQty: (compounds && compounds[i] && compounds[i].ingredientQty) || "",
                productId: (compounds && compounds[i] && compounds[i].productId) || "",
                productIdQlfr: "03"
            }));
        if (value > compoundIngredientCount) {
            handleCompoundIngredientChange(value, newCompounds)
        } else {
            changeCountConfimration(value, newCompounds)
        }
    }

    const handleCompoundIngredientChange = (compoundIngredientCount, compounds) => {
        onChange({target: {name: 'compoundIngredientCount', value: compoundIngredientCount}});
        onChange({target: {name: 'compounds', value: compounds}});
        closeModal();
    }

    const hasError = (i, field) => {
        return fieldState && fieldState[i] && fieldState[i][field]
        && errors && errors[i] && errors[i][field] && errors[i][field].isValid === false;
    }

    const hasErrorMsg = (i, field) => {
        return hasError(i, field) && errors[i][field].message;
    }

    const onBlur = e => {
        const {name: field, dataset: {row}} = e.target;
        setFieldState ( prev => {
            //console.log(prev)
            const change = prev ? [...prev] : [];
            if (!change[row]){
                change[row] = {};
            }
            change[row][field] = true;
            return change;
        })
    }

    const removeRow = row => {
        closeModal();
        let newCompounds = [...compounds];
        newCompounds.splice(row, 1);
        handleCompoundIngredientChange(compoundIngredientCount - 1, newCompounds);
    }

    const removeRowConfimration = row => {
        setModalState({...modalDefaults, open: true, confirm: () => removeRow(row)})
    }

    const rowUpdate = e => {
        const {name, value, dataset: {row}} = e.target;
        const newCompounds = [...compounds];
        newCompounds[row][name] = /\.\d{4}/.test(value) ? Number(value).toFixed(3) : value;
        handleCompoundIngredientChange(compoundIngredientCount, newCompounds);
    }

    return <Grid container id="compound-table">
            <Grid container className={classes.selectorContainer}>
                <Grid item xs={12} md={3}>
                    <RequiredInfo mode="asterisk"/>
                    Compound Ingredient Component Count: &nbsp;
                </Grid>
                <Grid item xs={12} md={7} className={classes.buffer}>
                    <SelectBox value={compoundIngredientCount} options={options}
                           onChange={compoundIngredientChange} name="compoundIngredientCount"/>
                </Grid>
            </Grid>
            {compounds && compounds.length &&
            <div>
                <table width="1000px">
                    <thead>
                    <tr>
                        <th width="40px"/>
                        <th width="200px" className={classes.center}><RequiredInfo mode='asterisk'/>ID Qualifier</th>
                        <th><RequiredInfo mode='asterisk'/>Product ID(NDC)</th>
                        <th><RequiredInfo mode='asterisk'/>Ingredient Quantity</th>
                        <th>
                            <AppButton text="Add Row" onClick={addRow} icon={<AddCircleIcon/>}
                                  disabled={compoundIngredientCount > 24 } className="btn-secondary"/>
                                  <br/>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {compounds.map((compound, i) => <tr key={i} valign="top">
                        <td>{i + 1}</td>
                        <td className={classes.center}>NDC</td>
                        <td>
                            <TextField variant="outlined" margin="dense" className={classes.compoundInput} name="productId"  data-row={i}
                                       value={compound.productId}
                                       error={hasError(i, 'productId')}
                                       helperText={hasErrorMsg(i, 'productId')}
                                       onBlur={onBlur}
                                       FormHelperTextProps={{style: { width: "28ch", color: "#a32d24" }}}
                                       inputProps={{
                                           "aria-label": `Product ID ${i+1}`,
                                           "data-row": i}} onChange={rowUpdate} required/>
                        </td>
                        <td>
                            <TextField variant="outlined" margin="dense" className={classes.compoundInput} data-row={i} name="ingredientQty"
                                       type="number"
                                       value={compound.ingredientQty}
                                       error={hasError(i, 'ingredientQty')}
                                       helperText={hasErrorMsg(i, 'ingredientQty')}
                                       onBlur={onBlur}
                                       FormHelperTextProps={{style: { width: "28ch", color: "#a32d24"}}}
                                        inputProps={{
                                            "aria-label": `Ingredient Quantity ${i+1}`,
                                            "data-row": i}} onChange={rowUpdate} required/>
                        </td>
                        <td>
                            <AppButton text="Remove&nbsp;&nbsp;" onClick={() => removeRowConfimration(i)}
                                  disabled={compoundIngredientCount < 3} icon={<RemoveCircleIcon/>}
                                       className="btn-danger"/>
                        </td>
                    </tr>)}
                    </tbody>
                </table>
            </div>
            }
    </Grid>;
}
